import axios, { AxiosResponse, AxiosError, InternalAxiosRequestConfig, isCancel } from 'axios';
import { useAuthStore } from '@/core/auth/auth';
import { storeToRefs } from 'pinia';
import { can } from '@/core/permissions/can';
import { permissionForRequest } from '@/core/permissions/registry';
import { useToast } from '@/shared/components/ui/toast/use-toast';


function isAbortedRequest(error: AxiosError): boolean {
  if (isCancel(error)) return true;
  const err = error as AxiosError & { code?: string; name?: string };
  return (
    err.code === 'ERR_CANCELED' ||
    err.name === 'CanceledError' ||
    err.name === 'AbortError'
  );
}

const instance = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

/**
 * Request gate:
 * - Maps method+url → permission key via registry
 * - Blocks if !can(key)
 * - Skip with config.skipPermissionCheck (or import skipPerm) for helper GETs
 *   needed by a screen the user already may open
 */
instance.interceptors.request.use(
  (config: InternalAxiosRequestConfig & { skipPermissionCheck?: boolean }) => {
    const authStore = useAuthStore();
    const { token } = storeToRefs(authStore);
    if (token.value) {
      config.headers['Authorization'] = `Bearer ${token.value}`;
    }

    const method = (config.method || 'GET').toUpperCase();
    const url = config.url || '';

    // Auth / session endpoints — never permission-gated
    const allowlist = [
      '/api/users/profile',
      '/api/users/refresh-token',
      '/api/users/logout',
      '/api/auth/refresh',
      '/api/auth/logout',
      '/api/users/forgot-password',
      '/api/users/verify-otp',
      '/api/users/reset-password'
    ];
    const isAllowlisted = allowlist.some(p => url.includes(p));

    if (!config.skipPermissionCheck && !isAllowlisted) {
      const required = permissionForRequest(method, url, config.data, config.params);
      const keys = Array.isArray(required) ? required : (required ? [required] : []);
      if (keys.length > 0 && !keys.some((k) => can(k))) {
        const { toast } = useToast();
        toast({
          title: 'Action Blocked',
          description: `You do not have permission: ${keys.join(' or ')}`,
          variant: 'destructive'
        });
        return Promise.reject(new Error(`Permission required: ${keys.join(' or ')}`));
      }
    }

    return config;
  },
  (error: AxiosError) => Promise.reject(error)
);

instance.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error: AxiosError) => {
    if (!error || !error.config) {
      return Promise.reject(error);
    }
    const authStore = useAuthStore();
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Skip interceptor for logout requests to prevent infinite loops
    if (originalRequest.url?.includes('/logout')) {
      return Promise.reject(error);
    }

    // Handle token refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      console.log('401 error detected on:', originalRequest.url);

      // Skip refresh for auth endpoints to prevent loops
      if (originalRequest.url?.includes('/login') ||
        originalRequest.url?.includes('/refresh-token') ||
        originalRequest.url?.includes('/signup')) {
        console.log('Skipping refresh for auth endpoint');
        return Promise.reject(error);
      }

      try {
        console.log('Attempting token refresh...');
        const newToken = await authStore.refreshToken();

        if (newToken) {
          console.log('Token refresh successful, retrying request:', originalRequest.url);
          if (originalRequest.headers) {
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
          }
          if (typeof originalRequest.data === 'string') {
            try {
              originalRequest.data = JSON.parse(originalRequest.data);
            } catch {
              // keep string if not valid JSON
            }
          }
          return instance(originalRequest);
        } else {
          // Refresh failed - already handled logout in refreshToken function
          console.log('Token refresh returned null, session ended');
          return Promise.reject(new Error('Session expired. Please login again.'));
        }
      } catch (refreshError) {
        // Refresh error - already handled logout in refreshToken function
        console.log('Token refresh threw error:', refreshError);
        return Promise.reject(new Error('Session expired. Please login again.'));
      }
    }

    if (!error.response) {
      if (isAbortedRequest(error)) {
        return Promise.reject(error);
      }
      console.error('Network Error:', error.message);
      return Promise.reject(new Error('Network Error: Unable to connect to the server'));
    }

    // Handle other error statuses
    switch (error.response.status) {
      case 400:
        console.error('Bad Request:', error.response.data);
        break;
      case 403:
        // Authenticated but not allowed for this route/role — do not logout (that would loop creditChecker etc.)
        console.error('Forbidden:', error.response.data);
        break;
      case 404:
        if ((error.response.data as any)?.error !== 'ServiceBaseIdNotFound') {
          console.error('Not Found:', error.response.data);
        }
        break;
      case 500:
        console.error('Internal Server Error:', error.response.data);
        break;
      default:
        console.error(`Unhandled error status: ${error.response.status}`, error.response.data);
    }

    return Promise.reject(error.response?.data || error);
  }
);

export default instance;
export { isCancel };